import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SHEETS, forceRefreshSheet } from "../utils/api";

// Each order tab maps to a backend sheet-cache key. The `refreshKey` is what
// /api/sheets/refresh/:key uses to push fresh data from Google Sheets into
// Mongo before we read — so a newly-placed order under a new firm is visible
// immediately instead of after the next periodic sync.
const GOOGLE_SCRIPT_URLS = [
  { key: "structure", url: SHEETS.orders("structure"), refreshKey: "orders_structure" },
  { key: "mm6", url: SHEETS.orders("tmt"), refreshKey: "orders_tmt" },
  { key: "rings", url: SHEETS.orders("rings"), refreshKey: "orders_rings" },
  { key: "binding", url: SHEETS.orders("binding"), refreshKey: "orders_binding" },
];

const ORDER_TABS = [
  { key: "all", label: "All" },
  { key: "structure", label: "Structure" },
  { key: "mm6", label: "TMT" },
  { key: "binding", label: "Binding Wire" },
  { key: "rings", label: "Rings" },
];

const normalizePhone = (v) =>
  String(v || "")
    .replace(/\D/g, "")
    .slice(-10);

const safeArray = (v) => (Array.isArray(v) ? v : []);

const cacheGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const { value, expiresAt } = parsed;
    if (expiresAt && Date.now() > expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return value ?? null;
  } catch {
    return null;
  }
};

const cacheSet = (key, value, ttlMs = 1000 * 60 * 5) => {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        value,
        expiresAt: ttlMs ? Date.now() + ttlMs : null,
      })
    );
  } catch {}
};

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.orders)) return data.orders;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
};

const pick = (obj, keys = []) => {
  if (!obj) return "";
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const parseNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const parseDateValue = (value) => {
  if (!value) return 0;

  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.getTime();

  const str = String(value).trim();
  const m = str.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (m) {
    let [, dd, mm, yyyy] = m;
    if (yyyy.length === 2) yyyy = `20${yyyy}`;
    const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  }

  return 0;
};

const formatDate = (value) => {
  const ts = parseDateValue(value);
  if (!ts) return value || "-";
  return new Date(ts).toLocaleDateString("en-IN");
};

const normalizeStatus = (raw) => {
  const s = String(raw || "").toLowerCase().trim();
  if (!s) return "Pending";
  if (["approved", "accepted"].includes(s)) return "Accepted";
  if (s === "rejected") return "Rejected";
  return "Pending";
};

const getStatusColor = (raw) => {
  const s = normalizeStatus(raw);
  if (s === "Accepted") return "bg-cyan-100 text-cyan-800";
  if (s === "Rejected") return "bg-red-100 text-red-800";
  return "bg-yellow-100 text-yellow-800";
};

const getProgressColor = (raw) => {
  const s = normalizeStatus(raw);
  if (s === "Accepted") return "from-cyan-400 to-cyan-600";
  if (s === "Rejected") return "from-red-400 to-red-600";
  return "from-yellow-400 to-yellow-500";
};

const getProgressPercent = (raw) =>
  normalizeStatus(raw) === "Pending" ? 0 : 100;

const getRejectedRemark = (row) =>
  pick(row, [
    "Status Remarks",
    "STATUS REMARKS",
    "Rejected Remark",
    "REJECTED REMARK",
    "REJECT REASON",
    "REJECTION REASON",
    "Rejection Reason",
  ]) || "";

const getRequestId = (row) =>
  pick(row, [
    "Request Id",
    "Request id",
    "REQUEST ID",
    "Unique Id",
    "Unique ID",
    "Order ID",
    "ORDER ID",
    "RequestID",
  ]);

const getTimestamp = (row) =>
  parseDateValue(
    pick(row, ["Timestamp", "timestamp", "Created At", "Date", "DATE"])
  );

const getPhoneFromRow = (row) =>
  normalizePhone(
    pick(row, [
      "Contact No",
      "Contact No ",
      "Contact Number",
      "Phone",
      "Mobile",
      "Userid",
      "GID",
      "Contact",
      "Mobile No",
      "Mobile Number",
      "Contact No.",
      "Contact Number ",
    ])
  );

// Firm name + GST MUST come from the same column group, or we end up
// showing one firm's name with a different firm's GST. Each pair below
// lists "firm key" → "gst keys to try (in order) for that pair".
const FIRM_GST_PAIRS = [
  {
    firm: ["Firm Name", "Firm Name "],
    gst: ["GST No", "GST No ", "  GST No  ", "GST", "Gst No", "GSTIN", "GSTIN/UIN"],
  },
  {
    firm: ["Second Firm Name", "Second Firm Name "],
    gst: ["Second Firm GST", "Second Firm GST ", "Second GST", "SecondGST"],
  },
  {
    firm: ["User Name", "User"],
    gst: ["GST", "GST No"],
  },
  {
    firm: ["SFirm Name"],
    gst: ["SGST", "GST"],
  },
];

function getFirmAndGst_(row) {
  if (!row || typeof row !== "object") return { firm: "", gst: "" };
  for (const pair of FIRM_GST_PAIRS) {
    for (const fk of pair.firm) {
      const fv = row[fk];
      if (fv !== undefined && fv !== null && String(fv).trim() !== "") {
        const firm = String(fv).trim();
        // Found the firm — now look up GST FROM THE SAME GROUP only
        for (const gk of pair.gst) {
          const gv = row[gk];
          if (gv !== undefined && gv !== null && String(gv).trim() !== "") {
            return { firm, gst: String(gv).trim().toUpperCase() };
          }
        }
        // Firm found but no matching GST in its group — return firm alone
        return { firm, gst: "" };
      }
    }
  }
  return { firm: "", gst: "" };
}

const getFirmName = (row) => getFirmAndGst_(row).firm;
const getGST = (row) => getFirmAndGst_(row).gst;

const getContactPerson = (row) =>
  pick(row, [
    "Contact Person",
    "Contact Person ",
    "User Name",
    "User",
    "SContact Person",
  ]);

const getPaymentTerms = (row) =>
  pick(row, ["Payment Terms", "Payment Terms ", "Pay Term", "Pay Terms"]);

const getDeliveryArea = (row) =>
  pick(row, [
    "Delivery Area",
    "Delivery Area ",
    "Area",
    "SAREA",
    "NEW S AREA",
    "SArea",
  ]);

const getVehicle = (row) => pick(row, ["Vehicle", "Vehicle "]);
const getDriverContact = (row) =>
  pick(row, ["Driver Contact", "Driver Contact "]);
const getLoadingDate = (row) => pick(row, ["Loading Date", "Loading Date "]);
const getRate = (row) => pick(row, ["Rate", "Rate "]);
const getNetBilling = (row) =>
  pick(row, ["Net Billing", "Net Basic", "NET BASIC"]);
const getOutstanding = (row) =>
  pick(row, ["Outstanding", "Outstanding ", "SOutstanding"]);
const getCreditLimit = (row) =>
  pick(row, ["Credit Limit", "Credit Limit ", "SCredit Limit  "]);
const getRemarks = (row) => pick(row, ["Remarks", "Remarks "]);
const getPlant = (row) => pick(row, ["New Plant", "Plant"]);
const getTransportation = (row) =>
  pick(row, ["New Transportation", "Transportation"]);
const getOrderStatus = (row) =>
  pick(row, ["Order Status", "ORDER STATUS", "Status"]);

const buildItemsFromRows = (rows, sourceType) => {
  const safeRows = safeArray(rows);

  if (sourceType === "mm6") {
    return safeRows.map((row) => ({
      label: pick(row, ["Item"]),
      qty: parseNum(pick(row, ["Qty"])),
      unit: "Kg",
      rate: getRate(row),
    }));
  }

  if (sourceType === "rings") {
    return safeRows.map((row) => ({
      label: pick(row, ["Size"]),
      qty: parseNum(pick(row, ["Qty(PCS)"])),
      unit: "Pcs",
      rate: getRate(row),
    }));
  }

  if (sourceType === "binding") {
    return safeRows.map((row) => ({
      label: pick(row, ["Size"]),
      qty: parseNum(pick(row, ["Qty"])),
      unit: "Kg",
      rate: getRate(row),
    }));
  }

  return safeRows.map((row) => ({
    label: pick(row, [
      "Item",
      "Item ",
      "Size",
      "Product",
      "Particular",
      "Particulars",
      "Description",
    ]),
    qty: parseNum(
      pick(row, [
        "Qty",
        "Qty ",
        "Quantity",
        "QTY",
        "Weight",
        "Weight(KGS)",
        "Qty(KGS)",
      ])
    ),
    unit: "Kg",
    rate: getRate(row),
  }));
};

const aggregateOrders = (rows = []) => {
  const groups = new Map();

  safeArray(rows).forEach((row, idx) => {
    const requestId = getRequestId(row) || `NO_ID_${idx}`;
    const sourceType = row.__sourceType || "unknown";
    const groupKey = `${sourceType}__${requestId}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey).push({ ...row, __sourceType: sourceType });
  });

  const aggregated = [];

  groups.forEach((groupRows, groupKey) => {
    const safeGroupRows = safeArray(groupRows);
    if (!safeGroupRows.length) return;

    const sortedRows = [...safeGroupRows].sort(
      (a, b) => getTimestamp(a) - getTimestamp(b)
    );
    const base = sortedRows[0];
    const sourceType = base.__sourceType;
    const requestId = getRequestId(base) || groupKey;

    const builtItems = buildItemsFromRows(safeGroupRows, sourceType);
    const items = Array.isArray(builtItems) ? builtItems : [];

    const totalQty =
      sourceType === "rings"
        ? items.reduce((sum, item) => sum + parseNum(item.qty), 0)
        : parseNum(pick(base, ["Total Qty"])) ||
          items.reduce((sum, item) => sum + parseNum(item.qty), 0);

    // Pair-aware extraction so firm + GST always come from the same
    // column group (prevents the "wrong GST shown for firm" bug where
    // firm came from "Second Firm Name" but GST leaked from "GST No").
    const _fgst = getFirmAndGst_(base);
    aggregated.push({
      requestId,
      timestamp: pick(base, ["Timestamp"]),
      sourceType,
      firmName: _fgst.firm,
      gst: _fgst.gst,
      contactPerson: getContactPerson(base),
      contactNo: getPhoneFromRow(base),
      paymentTerms: getPaymentTerms(base),
      deliveryArea: getDeliveryArea(base),
      vehicle: getVehicle(base),
      driverContact: getDriverContact(base),
      loadingDate: getLoadingDate(base),
      outstanding: getOutstanding(base),
      creditLimit: getCreditLimit(base),
      netBilling: getNetBilling(base),
      rate: getRate(base),
      remarks: getRemarks(base),
      plant: getPlant(base),
      transportation: getTransportation(base),
      status: getOrderStatus(base),
      rejectedRemark: getRejectedRemark(base),
      items: Array.isArray(items) ? items : [],
      totalQty,
      qtyUnit: sourceType === "rings" ? "Pcs" : "Kg",
      rawRows: Array.isArray(safeGroupRows) ? safeGroupRows : [],
    });
  });

  aggregated.sort(
    (a, b) => parseDateValue(b.timestamp) - parseDateValue(a.timestamp)
  );
  return aggregated;
};

export default function OrderStatusModal({ embedded, onBack }) {
  const loggedUser = useMemo(
    () => JSON.parse(localStorage.getItem("dealer") || "{}"),
    []
  );

  const loggedPhone = useMemo(
    () => normalizePhone(loggedUser?.phone),
    [loggedUser]
  );

  const CACHE_KEY = useMemo(
    () => (loggedPhone ? `ORDER_LIST_CACHE_${loggedPhone}` : ""),
    [loggedPhone]
  );

  const [orders, setOrders] = useState(() => {
    const cached = CACHE_KEY ? cacheGet(CACHE_KEY) : null;
    return Array.isArray(cached) ? cached : [];
  });

  const [loading, setLoading] = useState(() => {
    const cached = CACHE_KEY ? cacheGet(CACHE_KEY) : null;
    return !Array.isArray(cached);
  });

  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const [selectedFirms, setSelectedFirms] = useState([]);
  const [firmDropdownOpen, setFirmDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleBack = () => {
    if (typeof onBack === "function") return onBack();
    if (selectedOrder) return setSelectedOrder(null);
    window.history.back();
  };

  const filterMine = (rows) =>
    safeArray(rows).filter((row) => {
      const rowPhone = getPhoneFromRow(row);
      return rowPhone && rowPhone === loggedPhone;
    });

  const fetchAllOrders = async () => {
    // Push fresh sheet data into the backend cache BEFORE reading. All four
    // order sheets refresh in parallel so it's quick. Failures are
    // non-fatal — we still try to read whatever is cached.
    await Promise.allSettled(
      GOOGLE_SCRIPT_URLS.map(({ refreshKey }) => forceRefreshSheet(refreshKey))
    );

    const results = await Promise.allSettled(
      GOOGLE_SCRIPT_URLS.map(async ({ key, url }) => {
        const res = await fetch(url, { cache: "no-store" });
        const text = await res.text();
        const data = JSON.parse(text);
        const rows = asArray(data);

        return safeArray(rows).map((row) => ({
          ...row,
          __sourceType: key,
        }));
      })
    );

    const merged = [];
    results.forEach((result) => {
      if (result.status === "fulfilled" && Array.isArray(result.value)) {
        merged.push(...result.value);
      }
    });

    return aggregateOrders(filterMine(merged));
  };

  useEffect(() => {
    if (!loggedPhone) {
      setError("Loading");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    let ignore = false;

    const run = async () => {
      const cached = CACHE_KEY ? cacheGet(CACHE_KEY) : null;

      if (Array.isArray(cached) && cached.length) {
        setOrders(cached);
        setLoading(false);
        setError("");
      } else {
        setLoading(true);
      }

      try {
        setRefreshing(true);
        const finalOrders = await fetchAllOrders();

        if (ignore) return;

        setOrders(Array.isArray(finalOrders) ? finalOrders : []);

        if (!safeArray(finalOrders).length) {
          setError("No orders found yet. Place your first order!");
        } else {
          setError("");
        }

        if (CACHE_KEY) {
          cacheSet(
            CACHE_KEY,
            Array.isArray(finalOrders) ? finalOrders : [],
            1000 * 60 * 5
          );
        }
      } catch (err) {
        console.error(err);
        setError((prev) =>
          safeArray(orders).length ? prev : "Failed to load orders"
        );
      } finally {
        if (!ignore) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    run();

    return () => {
      ignore = true;
    };
  }, [loggedPhone, CACHE_KEY]); // eslint-disable-line

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFirmDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Each firm entry shows "FIRM NAME — GSTIN" so the dealer can
  // identify which firm by GST when they have multiple registrations.
  // Falls back to just the firm name when GST isn't on the order row.
  const firmOptions = useMemo(() => {
    const seen = new Map(); // firmName -> firstSeenGst
    safeArray(orders).forEach((order) => {
      const firm = String(order?.firmName || "").trim();
      if (!firm) return;
      if (!seen.has(firm)) {
        seen.set(firm, String(order?.gst || "").trim().toUpperCase());
      }
    });
    return Array.from(seen.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, gst]) => ({
        value: name,
        label: gst ? `${name} — ${gst}` : name,
      }));
  }, [orders]);

  const toggleFirm = (firm) => {
    setSelectedFirms((prev) =>
      prev.includes(firm)
        ? prev.filter((f) => f !== firm)
        : [...prev, firm]
    );
  };

  const filteredOrders = useMemo(() => {
    let allOrders = safeArray(orders);

    if (activeTab !== "all") {
      allOrders = allOrders.filter((order) => order?.sourceType === activeTab);
    }

    if (selectedFirms.length > 0) {
      allOrders = allOrders.filter((order) =>
        selectedFirms.includes(String(order?.firmName || "").trim())
      );
    }

    return allOrders;
  }, [orders, activeTab, selectedFirms]);

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );

  const DetailRow = ({ label, value }) => (
    <div className="bg-gray-50 rounded-xl p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-sm font-semibold text-gray-800 whitespace-pre-wrap break-words">
        {value || "-"}
      </p>
    </div>
  );

  const sourceLabel = (type) => {
    if (type === "mm6") return "6MM TMT";
    if (type === "rings") return "Rings";
    if (type === "binding") return "Binding Wire";
    return "Structure";
  };

  if (loading && !safeArray(orders).length) {
    return (
      <div className="bg-white rounded-3xl shadow-2xl p-10 text-center">
        <p className="text-xl text-gray-600">Loading your orders…</p>
      </div>
    );
  }

  if (error || !safeArray(orders).length) {
    return (
      <div className="bg-white rounded-3xl shadow-2xl p-10 text-center relative">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-800 font-[DM-sans]">
            My Order Request
          </h1>
          {embedded && (
            <button
              type="button"
              onClick={handleBack}
              className="text-3xl text-gray-500 hover:text-gray-800"
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
          )}
        </div>

        {refreshing ? (
          <p className="text-xs font-bold text-gray-500 mb-3">
            Refreshing latest orders…
          </p>
        ) : null}

        <p className="text-xl text-gray-500">{error || "No orders found"}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10 relative">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 font-[DM-sans]">
            My Order Request
          </h1>
          {refreshing ? (
            <p className="text-[11px] font-bold text-gray-500 mt-1">
              Refreshing latest orders…
            </p>
          ) : null}
        </div>

        {embedded && (
          <button
            type="button"
            onClick={handleBack}
            className="text-2xl md:text-4xl text-gray-500 hover:text-gray-800"
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {ORDER_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-bold border transition ${
                isActive
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="mb-6 relative" ref={dropdownRef}>
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Filter by Firm Name
        </label>

        <button
          type="button"
          onClick={() => setFirmDropdownOpen((prev) => !prev)}
          className="w-full md:w-[420px] min-h-[48px] px-4 py-3 border border-gray-300 rounded-xl bg-white text-left flex items-center justify-between shadow-sm hover:border-gray-400"
        >
          <span className="text-sm text-gray-700 truncate pr-3">
            {selectedFirms.length === 0
              ? "Select firm name(s)"
              : selectedFirms.join(", ")}
          </span>
          <span className="text-gray-500 text-sm">
            {firmDropdownOpen ? "▲" : "▼"}
          </span>
        </button>

        {firmDropdownOpen && (
          <div className="absolute z-20 mt-2 w-full md:w-[420px] bg-white border border-gray-200 rounded-2xl shadow-xl p-3 max-h-72 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-700">Firm Names</p>
              {selectedFirms.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFirms([])}
                  className="text-xs font-bold text-red-600 hover:text-red-700"
                >
                  Clear All
                </button>
              )}
            </div>

            {firmOptions.length === 0 ? (
              <p className="text-sm text-gray-500">No firm names found</p>
            ) : (
              <div className="space-y-2">
                {firmOptions.map((firm) => {
                  const checked = selectedFirms.includes(firm.value);
                  return (
                    <label
                      key={firm.value}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFirm(firm.value)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm text-gray-700 break-words">
                        {firm.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {selectedFirms.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedFirms.map((firm) => (
              <span
                key={firm}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold"
              >
                {firm}
                <button
                  type="button"
                  onClick={() => toggleFirm(firm)}
                  className="text-gray-500 hover:text-red-600"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {safeArray(filteredOrders).length === 0 ? (
        <div className="bg-gray-50 border rounded-2xl p-8 text-center text-gray-500">
          No orders found
          {activeTab !== "all"
            ? ` in ${ORDER_TABS.find((t) => t.key === activeTab)?.label}`
            : ""}
          {selectedFirms.length > 0 ? " for selected firm name(s)" : ""}.
        </div>
      ) : (
        <div className="space-y-4 md:space-y-8">
          {safeArray(filteredOrders).map((order, index) => {
            const items = safeArray(order?.items);
            const status = normalizeStatus(order?.status);
            const progress = getProgressPercent(order?.status);

            return (
              <motion.div
                key={`${order?.requestId || "NOID"}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border hover:shadow-xl cursor-pointer"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex justify-between gap-4 mb-2">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {order?.requestId || "-"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {formatDate(order?.timestamp)} •{" "}
                      {`${Number(order?.totalQty || 0).toLocaleString("en-IN")} ${
                        order?.qtyUnit || ""
                      }`}{" "}
                      •{" "}
                      {order?.netBilling
                        ? `₹${Number(order.netBilling).toLocaleString("en-IN")}`
                        : "-"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {sourceLabel(order?.sourceType)} • {items.length} item
                      {items.length > 1 ? "s" : ""}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-semibold text-gray-700">Firm:</span>{" "}
                      {order?.firmName || "-"}
                      {order?.gst && (
                        <>
                          {" • "}
                          <span className="font-semibold text-gray-700">GST:</span>{" "}
                          <span className="font-mono text-[11px]">
                            {order.gst}
                          </span>
                        </>
                      )}
                    </p>
                  </div>

                  <span
                    className={`px-2 py-1 rounded-[10px] h-[40px] font-bold ${getStatusColor(
                      order?.status
                    )}`}
                  >
                    {status}
                  </span>
                </div>

                {status === "Rejected" && (
                  <div className="mt-2 bg-red-50 border border-red-100 rounded-xl p-3">
                    <p className="text-xs font-bold text-red-700">
                      Rejected Remark
                    </p>
                    <p className="text-sm text-red-800 mt-1 whitespace-pre-wrap break-words">
                      {order?.rejectedRemark || "—"}
                    </p>
                  </div>
                )}

                <div className="mt-3 text-sm text-gray-700">
                  {items.slice(0, 2).map((item, idx) => (
                    <div key={idx}>
                      {item?.label || "-"} —{" "}
                      {Number(item?.qty || 0).toLocaleString("en-IN")}{" "}
                      {item?.unit || ""}
                    </div>
                  ))}
                  {items.length > 2 && (
                    <div className="text-xs text-gray-500 mt-1">
                      +{items.length - 2} more item(s)
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Submitted</span>
                    <span>{status}</span>
                  </div>
                  <div className="relative bg-gray-300 rounded-full h-3 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.8 }}
                      className={`absolute top-0 left-0 h-full bg-gradient-to-r ${getProgressColor(
                        order?.status
                      )}`}
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4"
          onClick={() => setSelectedOrder(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-800">
                Order Details
              </h2>
              <button
                className="text-2xl text-gray-500 hover:text-gray-800"
                onClick={() => setSelectedOrder(null)}
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              <Section title="Basic Information">
                <DetailRow label="Order ID" value={selectedOrder?.requestId} />
                <DetailRow
                  label="Order Date"
                  value={formatDate(selectedOrder?.timestamp)}
                />
                <DetailRow
                  label="Order Status"
                  value={normalizeStatus(selectedOrder?.status)}
                />
                <DetailRow
                  label="Order Type"
                  value={sourceLabel(selectedOrder?.sourceType)}
                />
              </Section>

              {normalizeStatus(selectedOrder?.status) === "Rejected" && (
                <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                  <h3 className="text-lg font-extrabold text-red-800">
                    Rejected Remark
                  </h3>
                  <p className="text-sm text-red-800 mt-2 whitespace-pre-wrap break-words">
                    {selectedOrder?.rejectedRemark || "—"}
                  </p>
                </div>
              )}

              <Section title="Firm Details">
                <DetailRow label="Firm Name" value={selectedOrder?.firmName} />
                <DetailRow label="GST No" value={selectedOrder?.gst} />
                <DetailRow
                  label="Contact Person"
                  value={selectedOrder?.contactPerson}
                />
                <DetailRow
                  label="Contact No"
                  value={selectedOrder?.contactNo}
                />
              </Section>

              <Section title="Delivery & Vehicle">
                <DetailRow label="Vehicle" value={selectedOrder?.vehicle} />
                <DetailRow
                  label="Driver Contact"
                  value={selectedOrder?.driverContact}
                />
                <DetailRow
                  label="Delivery Area"
                  value={selectedOrder?.deliveryArea}
                />
                <DetailRow
                  label="Loading Date"
                  value={formatDate(selectedOrder?.loadingDate)}
                />
              </Section>

              <Section title="Financial Summary">
                <DetailRow label="Rate" value={selectedOrder?.rate} />
                <DetailRow
                  label="Payment Terms"
                  value={selectedOrder?.paymentTerms}
                />
                <DetailRow
                  label="Outstanding"
                  value={selectedOrder?.outstanding}
                />
                <DetailRow
                  label="Credit Limit"
                  value={selectedOrder?.creditLimit}
                />
                <DetailRow
                  label="Net Billing"
                  value={selectedOrder?.netBilling}
                />
                <DetailRow
                  label="Transportation"
                  value={selectedOrder?.transportation}
                />
              </Section>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Items
                </h3>
                <div className="space-y-3">
                  {safeArray(selectedOrder?.items).map((item, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-xl p-4 border">
                      <div className="flex justify-between gap-4 flex-wrap">
                        <div className="font-semibold text-gray-800">
                          {item?.label || "-"}
                        </div>
                        <div className="text-sm text-gray-600">
                          {Number(item?.qty || 0).toLocaleString("en-IN")}{" "}
                          {item?.unit || ""}
                        </div>
                      </div>

                      {item?.rate ? (
                        <div className="text-sm text-gray-500 mt-1">
                          Rate: ₹{Number(item.rate).toLocaleString("en-IN")}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              <Section title="Remarks">
                <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-xl md:col-span-2">
                  {selectedOrder?.remarks || "—"}
                </p>
              </Section>
            </div>

            <div className="p-6 border-t text-right">
              <button
                onClick={() => setSelectedOrder(null)}
                className="px-6 py-2 rounded-xl bg-gray-800 text-white hover:bg-gray-900"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}